import { memo, useMemo } from 'react';
import { type Components } from 'react-markdown';

import { type MessageUploadAttachment } from '@apps/web/components/phoebe_chat/transcript_types';
import { Markdown, MarkdownAnchor } from '@apps/web/components/ui/markdown';
import {
    getAvatarSolidToneClass,
    UserAvatar,
} from '@apps/web/components/ui/user_avatar';
import { type V3PresentationContext } from '@apps/web/features/phoebe_chat_v3/model/presentation_context';
import { type V3MessageEntry } from '@apps/web/features/phoebe_chat_v3/model/transcript';
import { V3UserUploadAttachmentCard } from '@apps/web/features/phoebe_chat_v3/ui/chat_attachment_card';
import {
    linkEntityReferencesForMarkdown,
    resolveMarkdownEntityReference,
} from '@apps/web/lib/entity_references';
import { cn } from '@apps/web/lib/utils';

function V3UserMessageUploadAttachments({
    uploads,
    context,
}: {
    readonly uploads: ReadonlyArray<MessageUploadAttachment>;
    readonly context: V3PresentationContext;
}) {
    if (uploads.length === 0) {
        return null;
    }
    return (
        <div className="mt-2 flex flex-col gap-1">
            {uploads.map((upload) => (
                <V3UserUploadAttachmentCard
                    key={upload.attachmentId}
                    upload={upload}
                    context={context}
                />
            ))}
        </div>
    );
}

export const V3MessageRow = memo(function V3MessageRow({
    entry,
    presentationContext = null,
}: {
    readonly entry: V3MessageEntry;
    readonly presentationContext?: V3PresentationContext | null;
}) {
    const isUser = entry.role === 'user';
    const displayContent = linkEntityReferencesForMarkdown(entry.content);
    const markdownComponents = useMemo<Components>(
        () => ({
            a: ({ node, href, children, ...props }) => {
                const referenceDisplayName = resolveMarkdownEntityReference(
                    href,
                    entry.references,
                );
                if (referenceDisplayName !== null) {
                    return <span>{referenceDisplayName}</span>;
                }
                return (
                    <MarkdownAnchor
                        href={href}
                        className="text-[13px] leading-[18px] font-semibold text-[#5533EE] no-underline decoration-transparent hover:text-[#4629C7] hover:decoration-transparent dark:text-[#C9BFFA]"
                        {...props}
                    >
                        {children}
                    </MarkdownAnchor>
                );
            },
        }),
        [entry.references],
    );
    return (
        <div
            className={cn(
                'flex items-center gap-2.5',
                isUser ? 'justify-end' : 'justify-start',
            )}
        >
            <div
                data-testid="v3-message-content"
                aria-busy={entry.pendingDeliveryMode !== null}
                className={cn(
                    'min-w-0 text-sm leading-5 text-[#002429] [&_p]:my-0 [&_p]:text-sm [&_p]:leading-5',
                    isUser
                        ? 'max-w-[620px] rounded-[22px] bg-[#F2F2F2] px-4 py-[11px]'
                        : 'max-w-[700px]',
                    entry.isError && 'text-[#A8002E]',
                    entry.pendingDeliveryMode !== null &&
                        'motion-safe:animate-pulse motion-reduce:opacity-70',
                )}
            >
                <Markdown components={markdownComponents}>
                    {displayContent}
                </Markdown>
                {isUser &&
                entry.uploadAttachments &&
                entry.uploadAttachments.length > 0 &&
                presentationContext !== null ? (
                    <V3UserMessageUploadAttachments
                        uploads={entry.uploadAttachments}
                        context={presentationContext}
                    />
                ) : null}
            </div>
            {isUser && entry.author ? (
                <span
                    aria-label={`Sent by ${entry.author.displayName}`}
                    className="shrink-0"
                >
                    <UserAvatar
                        displayName={entry.author.displayName}
                        colorKey={entry.author.colorKey}
                        className="size-[34px]"
                        toneClassName={getAvatarSolidToneClass(
                            entry.author.colorKey,
                        )}
                    />
                </span>
            ) : isUser ? (
                <div aria-hidden={true} className="size-[34px] shrink-0" />
            ) : null}
        </div>
    );
});
